import { api } from './api';

/* Mirrors Eduvora.Application.Outcomes. ⚠ There is deliberately no money field anywhere in here. */

/** Serialised from the C# enum by index. */
export type RiskBand = 0 | 1 | 2 | 3;
export const BAND = { Urgent: 0, Watch: 1, Improving: 2, Steady: 3 } as const;

export interface SubjectGap {
  subject: string;
  studentPct: number;
  classPct: number;
  gapPct: number;
}

export interface StudentRisk {
  studentId: string;
  fullName: string;
  admissionNumber: string;
  classLabel: string;
  latestPct: number | null;
  previousPct: number | null;
  /** Change since the previous assessment cycle. Null when there is only one sitting. */
  delta: number | null;
  attendancePct: number | null;
  assessmentsMissed: number;
  band: RiskBand;
  pattern: string;
  headline: string;
  weakSubjects: SubjectGap[];
  actions: string[];
}

export interface ClassPattern {
  classLabel: string;
  subject: string;
  studentsAffected: number;
  classPct: number;
}

export interface RiskInstitution {
  id: string;
  name: string;
}

export interface RiskClassCount {
  classLabel: string;
  total: number;
  urgent: number;
}

export interface OutcomesReport {
  from: string;
  to: string;
  studentsAnalysed: number;
  urgent: number;
  watch: number;
  improving: number;
  /** ⚠ ONE PAGE, not the whole list. `matched` is how many there really are. */
  students: StudentRisk[];
  classPatterns: ClassPattern[];
  /** "subject" today. ⚠ Render this — do not let a reader assume topic-level precision. */
  granularity: string;
  caveat: string;
  institutions: RiskInstitution[];
  institutionId: string | null;
  /** ⚠ Counted BEFORE the class filter, so choosing one class does not empty the other chips. */
  classes: RiskClassCount[];
  matched: number;
  skip: number;
  /** ⚠ The analysis ran on a partial set. Say so on screen; never render it as if it were whole. */
  marksTruncated: boolean;
}

export interface RiskQuery {
  days?: number;
  institutionId?: string | null;
  band?: RiskBand | null;
  classLabel?: string | null;
  search?: string | null;
  skip?: number;
  take?: number;
}

/** The server parses the band by NAME, case-insensitively; index order matches the C# enum. */
const BAND_NAME = ['Urgent', 'Watch', 'Improving', 'Steady'] as const;

export const outcomesApi = {
  atRisk: (q: RiskQuery = {}) => {
    const to = new Date();
    const from = new Date(to.getTime() - (q.days ?? 90) * 86400000);
    const iso = (d: Date) => d.toISOString().slice(0, 10);

    const p = new URLSearchParams({ from: iso(from), to: iso(to) });
    if (q.institutionId) p.set('institutionId', q.institutionId);
    if (q.band !== null && q.band !== undefined) p.set('band', BAND_NAME[q.band]);
    if (q.classLabel) p.set('classLabel', q.classLabel);
    if (q.search) p.set('search', q.search);
    if (q.skip) p.set('skip', String(q.skip));
    if (q.take) p.set('take', String(q.take));

    return api.get<OutcomesReport>(`/api/outcomes/students-at-risk?${p.toString()}`);
  },
};
